import type { ReactNode } from 'react'
import { stripQuotedText } from '@/lib/email-body'
import type { CommunicationMessage } from '@/features/communications/types'

export function isAiMessage(m: CommunicationMessage) {
  return m.direction === 'system' || m.message_type === 'ai_summary'
}

function htmlToText(html: string) {
  if (typeof DOMParser === 'undefined') return html.replace(/<[^>]+>/g, ' ')
  const doc = new DOMParser().parseFromString(html, 'text/html')
  doc.querySelectorAll('style, script, head').forEach((node) => node.remove())
  return doc.body.textContent ?? ''
}

const LEADING_GREETING = /^(dear|hi|hello|hey|good (morning|afternoon))\b[^,\n]{0,40}[,!:]?\s*/i

/** The message's own words, flattened to one line, without quoted history or the greeting. */
export function messagePlainText(message: CommunicationMessage) {
  if (message.message_type === 'call' && message.body_text) {
    // A Quo call's text (USA-217): its summary and next steps, without headings, bullets or transcript.
    return message.body_text
      .split(/\n\s*Transcript:/)[0]!
      .replace(/^(Summary|Voicemail):\s*$/gm, '')
      .replace(/^- /gm, '')
      .replace(/\s+/g, ' ')
      .trim()
  }
  const text = message.body_text
    ? stripQuotedText(message.body_text)
    : message.body_html
      ? htmlToText(message.body_html)
      : ''
  const flat = text.replace(/\s+/g, ' ').trim()
  // A Quo call's text opens with its summary (USA-217): show the summary, not the heading.
  if (message.message_type === 'sms') return flat
  return flat.replace(LEADING_GREETING, '')
}

const EXCERPT_LEAD = 40

export function parseEmailList(raw: string | null | undefined): string[] {
  if (!raw) return []
  try {
    const parsed: unknown = JSON.parse(raw)
    if (Array.isArray(parsed)) {
      return parsed.filter((e): e is string => typeof e === 'string' && e.length > 0)
    }
    if (typeof parsed === 'string' && parsed.length > 0) return [parsed]
    return []
  } catch {
    return typeof raw === 'string' && raw.length > 0 ? [raw] : []
  }
}

// The senders mail servers report from; the backend's is_system_mailbox (USA-1704).
const MAIL_SERVER_SENDER = /^\s*(postmaster|mailer-daemon|microsoftexchange[0-9a-f]{32})@/i
const DELIVERY_FAILED_SUBJECT = /^(undeliverable:|delivery status notification \(failure\)|delivery failure)/i

/**
 * A mail server's report about one of our emails (a bounce): one line saying what happened and to whom.
 * Its body is the server's long how-to-fix page, shown only when staff open it (USA-1704).
 */
export function deliveryReport(message: CommunicationMessage): { label: string; recipients: string[] } | null {
  if (message.direction !== 'inbound' || !MAIL_SERVER_SENDER.test(message.from_email ?? '')) return null
  const subject = message.subject ?? ''
  const label = DELIVERY_FAILED_SUBJECT.test(subject)
    ? 'Not delivered'
    : /^delivery delayed/i.test(subject)
      ? 'Delivery delayed'
      : 'Mail server notice'
  return { label, recipients: failedRecipients(message) }
}

const isOurs = (address: string) => /@shopusars\.com\s*$/i.test(address) || MAIL_SERVER_SENDER.test(address)
// Where a report addressed to us names the address that failed; the backend's failed_recipients (USA-1704).
const FAILED_ADDRESS =
  /(?:delivered to|your message to|final-recipient:\s*rfc822;)\s*<?([\w.+'-]+@[\w-]+(?:\.[\w-]+)+)|<([\w.+'-]+@[\w-]+(?:\.[\w-]+)+)>:\s/gi

/** Most servers address a bounce to the address that failed; Gmail and some others address it to us. */
function failedRecipients(message: CommunicationMessage): string[] {
  const named = parseEmailList(message.to_emails).filter((address) => address.includes('@') && !isOurs(address))
  if (named.length > 0) return named
  const found: string[] = []
  for (const match of messagePlainText(message).toLowerCase().matchAll(FAILED_ADDRESS)) {
    const address = (match[1] ?? match[2] ?? '').replace(/\.$/, '')
    if (address && !isOurs(address) && !found.includes(address)) found.push(address)
  }
  return found
}

/**
 * What a collapsed message shows: the AI's summary of an inbound email, else its opening words.
 * While searching, a message whose summary lacks the search shows the words around the match.
 */
export function messagePreview(
  message: CommunicationMessage,
  query = '',
): { text: string; fromAi: boolean } {
  const report = deliveryReport(message)
  if (report) {
    const line = report.recipients.length ? `${report.label} to ${report.recipients.join(', ')}` : report.label
    if (!query || line.toLowerCase().includes(query)) return { text: line, fromAi: false }
  }
  const summary = message.ai_summary
  if (summary && (!query || summary.toLowerCase().includes(query))) {
    return { text: summary, fromAi: true }
  }
  const text = messagePlainText(message)
  const at = query ? text.toLowerCase().indexOf(query) : -1
  if (at > EXCERPT_LEAD) {
    const start = text.lastIndexOf(' ', at - EXCERPT_LEAD) + 1
    return { text: `…${text.slice(start)}`, fromAi: false }
  }
  if (at === -1 && summary) return { text: summary, fromAi: true }
  return { text, fromAi: false }
}

export function messageMatches(message: CommunicationMessage, query: string) {
  if (!query) return false
  const haystack = [
    message.subject,
    message.from_name,
    message.from_email,
    message.to_emails,
    message.cc_emails,
    message.ai_summary,
    messagePlainText(message),
  ]
    .filter(Boolean)
    .join('\n')
    .toLowerCase()
  return haystack.includes(query)
}

/** Normalized search text: trimmed and lowercased; empty means no search. */
export function normalizeQuery(raw: string) {
  return raw.trim().toLowerCase()
}

/** Wrap each occurrence of the query in a highlight. */
export function highlight(text: string, query: string): ReactNode {
  if (!query) return text
  const lower = text.toLowerCase()
  const parts: ReactNode[] = []
  let from = 0
  let at = lower.indexOf(query)
  while (at !== -1) {
    if (at > from) parts.push(text.slice(from, at))
    parts.push(
      <mark key={at} className="rounded-sm bg-amber-200/80 px-0.5 text-foreground dark:bg-amber-500/40">
        {text.slice(at, at + query.length)}
      </mark>,
    )
    from = at + query.length
    at = lower.indexOf(query, from)
  }
  if (from < text.length) parts.push(text.slice(from))
  return parts
}
